import { useEffect, useState, useCallback } from 'react'
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AspectRatio,
  Box,
  Flex,
  HStack,
  SimpleGrid,
  StackSeparator,
  Text,
  VStack,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import {
  CurrentVariantType,
  IComboGroup,
  IProduct,
  ISavedModifier,
} from '@/app/[locale]/_types'
import { useTranslations } from 'next-intl'
import { QuestionIcon, RightIcon } from '@/app/[locale]/_assets/icon-components'
import BottomSheet from '@/app/[locale]/_components/Sheet'
import ComboCard from '../ComboCard'
import Image from 'next/image'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import PreviewModal from '../Preview'
import { Button } from '@/components/ui/button'
import { useSelector } from 'react-redux'
import { IRedux } from '@/app/[locale]/_store/types'
import { usePathname } from '@/i18n/navigation'
import {
  fetchDirectCartModifiers,
  getOrderingDeliveryType,
  getSelectedMenuId,
} from '@/app/[locale]/_utils/modifier/buildCompulsoryModifiers'

function ComboProducts({
  comboData,
  currentVariant,
  onAddVariant,
}: {
  currentVariant: CurrentVariantType
  comboData: { groups: IComboGroup[] } | undefined
  onAddVariant: (
    group_id: string,
    variant: IProduct,
    mods: ISavedModifier[],
    quantity: number
  ) => void
}) {
  const [selectedGroup, setSelectedGroup] = useState<IComboGroup | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null)

  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { ssr: false }
  )
  const drawer = useDisclosure()
  const preview = useDisclosure()
  const currentLocale = useCustomLocale()
  const t = useTranslations()
  const pathname = usePathname()
  const { branch } = useSelector((state: IRedux) => state.common)
  const { is_hall, menu_id, order_type } = useSelector(
    (state: IRedux) => state.session
  )

  const selectedMenuId = getSelectedMenuId({
    isHall: is_hall === 'true',
    pathnameEndsWithMenu: pathname.endsWith('/menu'),
    menuId: menu_id,
    branchMenuId: branch?.menu_id,
  })

  const deliveryType = getOrderingDeliveryType({
    isHall: is_hall === 'true',
    orderType: order_type?.type,
  })

  const handleProductPreview = useCallback(
    (product: IProduct) => {
      setSelectedProduct(product)
      preview.onOpen()
    },
    [preview]
  )

  const handleVariantAdd = useCallback(
    async (variant: IProduct, group: IComboGroup) => {
      if (!variant.has_modifier) {
        onAddVariant(group.id, variant, [], group.quantity)
        if (!isDesktop) drawer.onClose()
        return
      }

      try {
        const directModifiers = await fetchDirectCartModifiers(
          variant.id,
          selectedMenuId,
          deliveryType
        )

        if (directModifiers) {
          onAddVariant(group.id, variant, directModifiers, group.quantity)
          if (!isDesktop) drawer.onClose()
          return
        }
      } catch {
        // Fall back to customize preview when modifier fetch fails.
      }

      setSelectedGroup(group)
      handleProductPreview(variant)
    },
    [
      drawer,
      handleProductPreview,
      isDesktop,
      onAddVariant,
      selectedMenuId,
      deliveryType,
    ]
  )

  useEffect(() => {
    if (!comboData) return

    const autoAddBasicVariants = async () => {
      for (const group of comboData.groups) {
        if (group.type !== 'combo_basic') continue

        for (const variant of group.variants ?? []) {
          if (!variant.has_modifier) {
            onAddVariant(group.id, variant, [], group.quantity)
            continue
          }

          try {
            const directModifiers = await fetchDirectCartModifiers(
              variant.id,
              selectedMenuId,
              deliveryType
            )

            if (directModifiers) {
              onAddVariant(group.id, variant, directModifiers, group.quantity)
            }
          } catch {
            // User can pick the variant manually in the drawer.
          }
        }
      }
    }

    void autoAddBasicVariants()
  }, [comboData, deliveryType, onAddVariant, selectedMenuId])

  const handleGroupClick = (group: IComboGroup) => {
    setSelectedGroup(group)
    drawer.onOpen()
  }

  const handleAddVariant = (mods: ISavedModifier[]) => {
    if (selectedGroup && selectedProduct) {
      onAddVariant(
        selectedGroup.id,
        selectedProduct,
        mods,
        selectedGroup.quantity
      )
    }
    preview.onClose()
  }

  const renderComboCard = (variant: IProduct, group: IComboGroup) => (
    <ComboCard
      key={variant.id}
      product={variant}
      selectedVariant={currentVariant?.[group.id]}
      onAdd={() => {
        void handleVariantAdd(variant, group)
      }}
    />
  )

  return (
    <>
      {comboData?.groups?.length && (
        <VStack
          separator={<StackSeparator borderColor='#0000000D' />}
          my={{ base: 2, lg: 4 }}
          colorScheme='primary'
          gap={0}
        >
          {comboData.groups.map((group) => (
            <HStack
              onClick={() => handleGroupClick(group)}
              justifyContent='space-between'
              cursor={'pointer'}
              key={group.id}
              w='full'
              py={3}
              px={4}
            >
              <Flex alignItems='center' gap={4}>
                {currentVariant?.[group.id]?.image ? (
                  <AspectRatio ratio={1 / 1} h={'60px'} w={'60px'} rounded={16}>
                    <Image
                      src={`${process.env.BASE_URL}${
                        currentVariant[group.id]?.image || ''
                      }`}
                      style={{
                        borderRadius: '16px',
                      }}
                      alt='select product'
                      fill
                    />
                  </AspectRatio>
                ) : (
                  <Box bg='background' p='14px' rounded={16}>
                    <QuestionIcon />
                  </Box>
                )}
                <Box>
                  <Text fontSize='sm' fontWeight={500}>
                    {group.title?.[currentLocale]}
                  </Text>
                  <Text fontSize='xs' color='text.muted'>
                    {currentVariant?.[group.id]?.variant_name || t('select')}
                  </Text>
                </Box>
              </Flex>
              {!currentVariant?.[group.id] && <RightIcon />}
            </HStack>
          ))}
        </VStack>
      )}
      <VariantDialog
        open={drawer.open}
        onOpenChange={(e) => drawer.setOpen(e)}
        selectedGroup={selectedGroup}
      >
        <SimpleGrid columns={2} gap={2} mt={4}>
          {selectedGroup?.variants?.map((variant) =>
            renderComboCard(variant, selectedGroup)
          )}
        </SimpleGrid>
        {selectedProduct && selectedGroup && (
          <PreviewModal
            selectedVariant={currentVariant?.[selectedGroup?.id]}
            data={selectedProduct}
            isOpen={preview.open}
            onOpenChange={preview.setOpen}
            onAdd={handleAddVariant}
          />
        )}
      </VariantDialog>
    </>
  )
}

const VariantDialog = ({
  open = false,
  onOpenChange,
  children,
  selectedGroup,
}: {
  open: boolean
  onOpenChange: (e: boolean) => void
  selectedGroup: IComboGroup | null
  children: React.ReactNode
}) => {
  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { ssr: false }
  )
  const currentLocale = useCustomLocale()
  const t = useTranslations()

  if (isDesktop)
    return (
      <DialogRoot
        open={open}
        onOpenChange={(e) => onOpenChange(e.open)}
        placement='center'
      >
        <DialogBackdrop />
        <DialogTrigger />
        <DialogContent maxW={'392px'} borderRadius={12} color='text'>
          <DialogCloseTrigger _hover={{ bg: 'background' }} color='text' />
          <DialogHeader>
            <DialogTitle textAlign='center' fontWeight={700} fontSize='lg'>
              {selectedGroup?.title?.[currentLocale]}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>{children}</DialogBody>
          <DialogFooter pb={6}>
            <DialogActionTrigger asChild>
              <Button
                h='56px'
                flex={1}
                bg='primary'
                color='primary.text'
                _hover={{ bg: 'primary.hover' }}
                rounded={16}
                fontSize={20}
                fontWeight={600}
              >
                {t('ready')}
              </Button>
            </DialogActionTrigger>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    )
  return (
    <BottomSheet
      onClose={() => onOpenChange(false)}
      contentHeight='full'
      isOpen={open}
      zIndex={1401}
    >
      <Box px={4} h={'full'} color='text'>
        <Text fontSize='lg' fontWeight='bold'>
          {selectedGroup?.title?.[currentLocale]}
        </Text>
        {children}
      </Box>
    </BottomSheet>
  )
}

export default ComboProducts
